<template>
  <div>
    <Layout :containers="containers">
      <template v-slot:header>
        <HomeHeroBar
          :section="'home2'"
          :trailingBold="true"
          @quickStartCardLinkClicked="quickStartCardLinkClicked"
          @gradientButtonClicked="signupClicked"
        />
      </template>
      <template v-slot:info-tiles>
        <div class="section-padding">
          <InfoTilesContainer
            :tiles="infoTiles"
            :alternativeTitle="`What's included <span style='
              font-family: Montserrat;
              font-size: 28px;
              font-weight: 500;
              line-height: 34px;
              letter-spacing: -0.0025em;
              text-align: center;'>for free?</span>`"
            :additionalText="`<strong>Your will is free.</strong> When you create your will with us, Old Mutual will be appointed 
            as the Executor of your estate. To manage your estate, we apply the standard executor fee – 
            ensuring your wishes are carried out professionally and your family is supported.`"
          />
        </div>
      </template>
      <template v-slot:button-tiles>
        <div class="section-padding mobile-padding-top">
          <ButtonTilesContainer :tiles="buttonTiles" :simpleTiles="true" />
        </div>
      </template>
      <template v-slot:we-believe>
        <div class="section-padding">
          <ImageTextBanner
            :imageUrl="whitelabel.images.home2WeBelieveImage"
            :title="'We believe every South African deserves access to a legally valid will.'"
            :paragraphOne="`Our free will creation service makes that possible - professionally supported, fully compliant,
and with no upfront costs.`"
            :reversed="true"
            :bottomMargin="true"
          />
        </div>
      </template>
      <template v-slot:create-your-will>
        <CreateYourWillBanner
          :alternativeBanner="true"
          :bannerText="`<strong style='font-weight:500;'>Join 23,000+ South Africans</strong> who’ve created their wills online with Old Mutual since 2023`"
          :pb0="true"
        />
      </template>
      <template v-slot:faqs>
        <div class="section-padding"></div>
        <TitleRow
          :html="true"
          :title="`Frequently Asked Questions`"
          :description="''"
        />
        <Accordion :accordionData="accordionData" :html="true" />
      </template>
      <template v-slot:protect-what-matters>
        <CreateYourWillBanner
          :alternativeBanner="true"
          :bannerText="`Protect what matters most. <strong>Start your free will today - free, secure, and legal.</strong>`"
          :buttonText="'START YOUR FREE WILL'"
          @createYourWill="createYourWill"
          :pb0="true"
        />
      </template>
      <template v-slot:callusback>
        <EstateAdministrationCostCalculatorFooter
          @requestACall="requestACall"
          v-if="whitelabel.brand !== 'quickwill'"
        />
      </template>
    </Layout>
    <SuccessSnackbar v-model="showAlert" :message="alertMessage" />
    <RequestACallDialog
      :showRequestACallDialog.sync="showRequestACallDialog"
      @success="showSuccessMessage"
    />
  </div>
</template>

<script>
  import HomeHeroBar from './HomeHeroBar.vue';
  import ButtonTilesContainer from '../ButtonTilesContainer.vue';
  import InfoTilesContainer from '../InfoTilesContainer.vue';
  import CreateYourWillBanner from '../CreateYourWillBanner.vue';
  import ImageTextBanner from '../ImageTextBanner.vue';
  import TestimonialsSection from '../TestimonialsSection.vue';
  import HR from '../../common/HR.vue';
  import Layout from '../../common/layouts/Layout.vue';
  import DynamicNavDrawer from '../../guest/DynamicNavDrawer.vue';
  import DrawerSignUp from '../../guest/DrawerSignUp.vue';
  import DrawerSignIn from '../../guest/DrawerSignIn.vue';
  import SuccessSnackbar from '../../common/SuccessSnackbar.vue';
  import EstateAdministrationCostCalculatorFooter from '../EstateCostCalculator/EstateAdministrationCostCalculatorFooter.vue';
  import RequestACallDialog from '../dialogs/RequestACallDialog.vue';
  import Accordion from '../../common/Accordion.vue';
  import TitleRow from '../../common/TitleRow.vue';

  export default {
    name: 'Home2',
    metaInfo() {
      return {
        link: [
          {
            rel: 'canonical',
            href: this.whitelabel.canonicalUrls.homepage,
          },
        ],
      };
    },
    props: {
      openSignupDrawerImmediately: {
        required: false,
        default: false,
        type: Boolean,
      },
      isReferralLink: {
        required: false,
        default: false,
        type: Boolean,
      },
      isActivationLink: {
        required: false,
        default: false,
        type: Boolean,
      },
      isPasswordResetLink: {
        required: false,
        default: false,
        type: Boolean,
      },
      verifyUserLinkCode: {
        required: false,
        default: null,
        type: Object,
      },
    },
    components: {
      HomeHeroBar,
      ButtonTilesContainer,
      InfoTilesContainer,
      CreateYourWillBanner,
      ImageTextBanner,
      TestimonialsSection,
      DynamicNavDrawer,
      HR,
      Layout,
      DrawerSignUp,
      DrawerSignIn,
      SuccessSnackbar,
      EstateAdministrationCostCalculatorFooter,
      RequestACallDialog,
      Accordion,
      TitleRow,
    },
    data(props) {
      return {
        signUpDrawerOpen: props.openSignupDrawerImmediately,
        signInDrawerOpen: false,
        showAlert: false,
        alertMessage: null,
        countryPattern: require('@/assets/shared/redesign/backgrounds/country-pattern.svg'),
        greenAccentOne: require('@/assets/shared/redesign/backgrounds/green-accent-1.svg'),
        greenAccentTwo: require('@/assets/shared/redesign/backgrounds/green-accent-2.svg'),
        purpleAccent: require('@/assets/shared/redesign/backgrounds/purple-accent.svg'),
        tribalPattern: require(`@/assets/old_mutual/homepage/generic.svg`),
        showRequestACallDialog: false,
      };
    },
    mounted() {
      if (localStorage.getItem('isLandingSimple')) {
        localStorage.removeItem('isLandingSimple');
        location.reload();
      }
    },
    methods: {
      successResetPassword() {
        this.signInDrawerOpen = false;
        this.alertMessage =
          this.whitelabel.authentication.resetPassword.successMessage;
        this.showAlert = true;
      },
      loginClicked() {
        this.signUpDrawerOpen = false;
        this.signInDrawerOpen = true;
      },
      signupClicked() {
        this.$router.push('/register');
      },
      drawerClosed() {
        this.signUpDrawerOpen = false;
      },
      signInDrawerClosed() {
        this.signInDrawerOpen = false;
      },
      quickStartCardLinkClicked(routeName) {
        if (routeName === 'SignUpPage') {
          this.signUpDrawerOpen = true;
        } else {
          this.$router.push({ name: routeName });
        }
      },
      createYourWill() {
        this.$router.push('/register');
      },
      estateCostCalculator() {
        this.$router.push({
          name: 'EstateAdministrationCostCalculator',
        });
      },
      requestACall() {
        this.showRequestACallDialog = true;
      },
      showSuccessMessage(success) {
        this.$emit('success', success);
      },
    },
    computed: {
      containers() {
        return [
          {
            name: 'header',
            background: 'countryPattern',
            class: 'hero-banner',
          },
          {
            name: 'info-tiles',
            hr: true,
            fadedRow: true,
            background: 'countryPattern',
          },
          {
            name: 'button-tiles',
            hr: true,
          },
          {
            name: 'we-believe',
          },
          {
            name: 'create-your-will',
          },
          {
            name: 'faqs',
          },
          {
            name: 'protect-what-matters',
          },
          { name: 'callusback' },
        ];
      },
      buttonTiles() {
        return [
          {
            imageUrl: this.whitelabel.images.home1ButtonTileOne,
            title: '',
            description: `Answer a few guided questions`,
          },
          {
            imageUrl: this.whitelabel.images.home1ButtonTileTwo,
            title: '',
            description: `Review your personalised will`,
          },
          {
            imageUrl: this.whitelabel.images.home1ButtonTileThree,
            title: '',
            description: `Print, sign and validate`,
          },
        ];
      },
      infoTiles() {
        return [
          {
            imageUrl: this.whitelabel.images.home2InfoTitleOne,
            title: 'Draft your will online',
            description: '',
          },
          {
            imageUrl: this.whitelabel.images.home2InfoTitleTwo,
            title: 'Store it securely for free',
            description: '',
          },
          {
            imageUrl: this.whitelabel.images.home2InfoTitleThree,
            title: 'Update it any time',
            description: '',
          },
          {
            imageUrl: this.whitelabel.images.home2InfoTitleFour,
            title: 'Get support from experts',
            description: '',
          },
        ];
      },
      accordionData() {
        return this.whitelabel.FAQs2;
      },
    },
  };
</script>
<style lang="scss" scoped>
  @media only screen and (min-width: 601px) and (max-width: 807px) {
    .mobile-padding-top {
      padding-top: 111px;
    }
  }
  @media only screen and (min-width: 0px) and (max-width: 600px) {
    .mobile-padding-top {
      padding-top: 133px;
    }
  }
</style>
